// Translation files are auto-generated via n8n workflow.
// Do not manually edit non-English files.

// Darija is intentionally NOT a user-selectable UI language (no full
// UI translation exists). It is still registered as an i18next
// resource below so Malak AI card components can render Darija
// strings when the conversation locale is Darija.
export const SUPPORTED_LANGUAGES = ['en', 'se', 'fr', 'es', 'it', 'ar'] as const;

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// ── Two-stage locale loading ────────────────────────────────────
// Locale JSONs are 600–800 KB each, so each language is both a
// separate chunk (only the active language is fetched) AND split in
// two by `vite/localeSplitPlugin.mjs`:
//   critical → app shell + public above-the-fold copy (blocks render)
//   rest     → everything else, merged into the SAME i18next store
//              immediately after first paint.
// There is no second translation engine: the rest bundle is added via
// `addResourceBundle`, so keys, fallbacks, RTL and switching are unchanged.
const localeLoaders: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  en: () => import('virtual:locale/en/critical'),
  se: () => import('virtual:locale/se/critical'),
  fr: () => import('virtual:locale/fr/critical'),
  es: () => import('virtual:locale/es/critical'),
  it: () => import('virtual:locale/it/critical'),
  ar: () => import('virtual:locale/ar/critical'),
  darija: () => import('virtual:locale/darija/critical'),
};

const restLoaders: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  en: () => import('virtual:locale/en/rest'),
  se: () => import('virtual:locale/se/rest'),
  fr: () => import('virtual:locale/fr/rest'),
  es: () => import('virtual:locale/es/rest'),
  it: () => import('virtual:locale/it/rest'),
  ar: () => import('virtual:locale/ar/rest'),
  darija: () => import('virtual:locale/darija/rest'),
};

const lazyBackend = {
  type: 'backend' as const,
  read(
    language: string,
    _namespace: string,
    callback: (err: unknown, data?: Record<string, unknown>) => void,
  ) {
    const load = localeLoaders[language] ?? localeLoaders.en;
    load().then(
      (mod) => callback(null, (mod.default ?? mod) as Record<string, unknown>),
      (err) => callback(err, undefined),
    );
  },
};

/**
 * Languages whose deferred half has not been merged yet. Seeded eagerly so the
 * very first render already knows translations are still incoming.
 */
const restPending = new Set<string>(['en']);

export const i18nReady = i18n
  .use(lazyBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    // While the deferred half is still in flight a key may legitimately be
    // missing for a few hundred ms. Render nothing rather than flashing a raw
    // key like `faq.badge`; the value appears as soon as the bundle merges.
    //
    // i18next also routes keys that RESOLVED VIA `defaultValue` through this
    // handler, so the caller's fallback text must be honoured first —
    // otherwise every `t(key, { defaultValue })` call (storefront section
    // headings, builder labels) prints the raw key instead of its copy.
    parseMissingKeyHandler: (key: string, defaultValue?: string) => {
      if (typeof defaultValue === 'string' && defaultValue !== '') return defaultValue;
      return restPending.size > 0 ? '' : key;
    },
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      order: ['localStorage'],
      caches: ['localStorage'],
    },
    react: {
      // Translations load asynchronously per language; never suspend
      // mid-tree — components render with fallback keys briefly instead.
      useSuspense: false,
      // Re-render once the deferred half of the locale is merged in.
      bindI18n: 'languageChanged loaded added',
      // `added` is emitted by the resource STORE, not by the i18n instance, so
      // without this binding a component only picked up the deferred half of a
      // locale on the next navigation or a manual page refresh.
      bindI18nStore: 'added removed',
    },
  });

const hydrated = new Map<string, Promise<void>>();

/** Merges the deferred half of a locale into the existing i18next store. */
export function hydrateLocaleRest(language?: string): Promise<void> {
  const lang = (language || i18n.language || 'en').split('-')[0];
  const load = restLoaders[lang];
  if (!load) return Promise.resolve();
  const existing = hydrated.get(lang);
  if (existing) return existing;
  restPending.add(lang);
  const task = load()
    .then((mod) => {
      const data = (mod.default ?? mod) as Record<string, unknown>;
      i18n.addResourceBundle(lang, 'translation', data, true, true);
    })
    .catch(() => {
      hydrated.delete(lang);
    })
    .finally(() => {
      restPending.delete(lang);
    });
  hydrated.set(lang, task);
  return task;
}

/**
 * Warms a language before the user commits to it (menu open / hover), so the
 * actual switch is instant instead of waiting on two chunk downloads.
 */
export function preloadLanguage(language: string): void {
  const lang = (language || 'en').split('-')[0];
  const critical = localeLoaders[lang];
  if (critical) void critical().catch(() => undefined);
  void hydrateLocaleRest(lang);
}

// Keep the English fallback complete too, and follow language switches.
i18n.on('languageChanged', (lng) => {
  const lang = (lng || 'en').split('-')[0];
  // Direction + <html lang> must follow the language everywhere (portal,
  // storefront, public site) — not only on the screens that set it themselves.
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' || lang === 'darija' ? 'rtl' : 'ltr';
  }
  void hydrateLocaleRest(lang);
});

/** Resolves once the active language (and the English fallback) is complete. */
export const i18nFullyLoaded = i18nReady.then(() =>
  Promise.all([hydrateLocaleRest(), hydrateLocaleRest('en')]).then(() => undefined),
);

export default i18n;
